import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { of, from } from 'rxjs';
import { UserService } from '../../../core/services/user.service';
import { UserActions } from './user.actions';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { getQueries, selectImportUserData } from './user.selectors';
import { AlertService } from 'src/app/core/services/alert.service';
import { FileUploadActions } from '../file-upload/file-upload.actions';

@Injectable()
export class UserEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly store: Store,
    private readonly alertService: AlertService
  ) {}

  loadUsersList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.listUsers),
      withLatestFrom(this.store.select(getQueries)),
      mergeMap(([, filters]) => {
        return from(
          this.userService.listUsers(
            filters.body ?? {},
            filters.params.page,
            filters.params.limit
          )
        ).pipe(
          map((users) => {
            return UserActions.listUsersSuccess({
              response: users,
              filters,
            });
          }),
          catchError((error) =>
            of(UserActions.listUsersFailure({ error: error.message }))
          )
        );
      })
    )
  );

  loadUserNamesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.listUserNames),
      mergeMap(() => {
        return from(this.userService.listUserNames()).pipe(
          map((users) => {
            return UserActions.listUserNamesSuccess({
              response: users,
            });
          }),
          catchError((error) =>
            of(UserActions.listUserNamesFailure({ error: error.message }))
          )
        );
      })
    )
  );

  loadActiveSuperVisorsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.listActiveSuperVisorsNames),
      mergeMap(() => {
        return from(this.userService.listActiveSuperVisors()).pipe(
          map((users) => {
            return UserActions.listActiveSuperVisorsNamesSuccess({
              response: users,
            });
          }),
          catchError((error) =>
            of(
              UserActions.listActiveSuperVisorsNamesFailure({
                error: error.message,
              })
            )
          )
        );
      })
    )
  );
  loadAllSuperVisorsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.listAllSuperVisorsNames),
      mergeMap((action) => {
        return from(
          this.userService.listAllSuperVisors(
            action.request.client_ids,
            action.request.status,
            action.request.type
          )
        ).pipe(
          map((users) => {
            return UserActions.listAllSuperVisorsNamesSuccess({
              response: users,
            });
          }),
          catchError((error) =>
            of(
              UserActions.listAllSuperVisorsNamesFailure({
                error: error.message,
              })
            )
          )
        );
      })
    )
  );

  loadSingleUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadSingleUser),
      mergeMap((action) =>
        from(this.userService.getUserById(action.id)).pipe(
          mergeMap((user) => {
            const actions = [];
            actions.push(
              UserActions.loadSingleUserSuccess({ user: user.data })
            );

            if (action.isUserPermission && user.data.permissions) {
              actions.push(
                UserActions.loadUserPermissionSuccess({
                  userPermission: user.data,
                })
              );
            }
            return actions;
          }),
          catchError((error) =>
            of(UserActions.loadSingleUserFailure({ error: error.message }))
          )
        )
      )
    )
  );

  createUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.createUser),
      mergeMap((action) =>
        from(this.userService.createUser(action.user)).pipe(
          map((response) => {
            this.alertService.success(response.message, 'Success');
            return UserActions.createUserSuccess({
              user: response.data,
              step: action.step,
            });
          }),
          catchError((error) => {
            return of(UserActions.createUserFailure({ error: error.message }));
          })
        )
      )
    )
  );
  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateUser),
      mergeMap((action) =>
        from(this.userService.updateUser(action.id, action.user)).pipe(
          map((response) => {
            this.alertService.success(response.message, 'Success');
            return UserActions.updateUserSuccess({
              user: response.data,
              step: action.step,
            });
          }),
          catchError((error) =>
            of(UserActions.updateUserFailure({ error: error.message }))
          )
        )
      )
    )
  );

  updateRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateUserRoles),
      mergeMap((action) =>
        from(this.userService.updateRole(action.id, action.role)).pipe(
          map((response) => {
            this.alertService.success(response.message, 'Success');

            return UserActions.updateUserRolesSuccess({
              user: response.data,
              step: action.step,
            });
          }),
          catchError((error) =>
            of(UserActions.updateUserRolesFailure({ error: error.message }))
          )
        )
      )
    )
  );

  refreshDataOnChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        UserActions.createUserSuccess,
        UserActions.updateUserSuccess,
        UserActions.updateUserRolesSuccess,
        UserActions.deleteUserSuccess,
        UserActions.changeUserStatusSuccess
      ),
      mergeMap((action) => {
        if (
          action.type === UserActions.createUserSuccess.type ||
          action.type === UserActions.updateUserSuccess.type
        ) {
          if (action.step !== 1) {
            return of(UserActions.loadSingleUser({ id: action.user._id }));
          }
        }

        return of(UserActions.listUsers());
      })
    )
  );

  redirectAfterUserChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        UserActions.createUserSuccess,
        UserActions.updateUserSuccess,
        UserActions.updateUserRolesSuccess
      ),
      tap((action) => {
        if (action.step === 1) {
          this.router.navigate(['/users'], {
            queryParamsHandling: 'merge',
            queryParams: { step: 1 },
          });
        } else {
          this.router.navigate(['/users/edit', action.user._id], {
            queryParams: { step: action.step },
            queryParamsHandling: 'merge',
          });
        }
      }),
      mergeMap((action) => {
        console.log('action222', action);
        if (action.step === 1) {
          return of(UserActions.clearSingleUser());
        } else {
          return of(
            UserActions.loadSingleUser({
              id: action.user._id,
            })
          );
        }
      })
    )
  );
  uploadProfilePicture$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileUploadActions.uploadFileSuccess),
      mergeMap(({ url, fileName }) => {
        return of(
          UserActions.updateProfilePicture({
            url,
            fileName,
          })
        );
      })
    )
  );

  redirectAfterCancelForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.clearSingleUser),
        tap(() =>
          this.router.navigate(['/users'], {
            queryParamsHandling: 'merge',
            queryParams: { step: 1 },
          })
        )
      ),
    { dispatch: false }
  );

  deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.deleteUser),
      mergeMap((action) =>
        from(this.userService.deleteUser(action.id)).pipe(
          map((response) => {
            action.closeModalSignal.set(true);
            this.alertService.success(response.message, 'Success');
            return UserActions.deleteUserSuccess({ id: action.id });
          }),
          catchError((error) =>
            of(UserActions.deleteUserFailure({ error: error.message }))
          )
        )
      )
    )
  );

  changeStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.changeUserStatus),
      mergeMap((action) =>
        from(this.userService.changeUserStatus(action.id)).pipe(
          map((response) => {
            this.alertService.success(response.message, 'Success');
            return UserActions.changeUserStatusSuccess();
          }),
          catchError((error) =>
            of(
              UserActions.changeUserStatusFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );

  importUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.importUsers),
      mergeMap((action) =>
        from(
          this.userService.importUsers(
            action.file,
            action.clientIds,
            action.programIds,
            action.enableIpBasedAccess
          )
        ).pipe(
          map((response) => {
            console.log('Import Users Response', response);
            this.alertService.success(response.message, 'Success');
            this.router.navigate(['/users/import-results']);
            return UserActions.importUsersSuccess({
              message: response.message,
              data: response.data,
            });
          }),
          catchError((error) => {
            console.log('Import Users Error', error);
            return of(UserActions.importUsersFailure({ error: error.message }));
          })
        )
      )
    )
  );

  exportErrorsTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.exportErrorsTemplate),
      withLatestFrom(this.store.select(selectImportUserData)),
      mergeMap(([, Importerrordata]) =>
        from(
          this.userService.exportErrorsTemplate({
            data: Importerrordata?.errors,
          })
        ).pipe(
          map((response) => {
            this.alertService.success('Exported successfully', 'Success');
            const timestamp = Date.now(); // current time in milliseconds
            const fileName = `failed_records_export${timestamp}.xlsx`;
            const blob = new Blob([response], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = globalThis.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            globalThis.URL.revokeObjectURL(url);

            return UserActions.exportErrorsTemplatesuccess();
          }),
          catchError((error) => {
            console.error('Export Errors Template Error', error);
            this.alertService.error(
              'Failed to export errors template',
              'Error'
            );
            return of(
              UserActions.exportErrorsTemplateFailure({ error: error.message })
            );
          })
        )
      )
    )
  );

  downloadUsersTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.downloadUsersTemplate),
      mergeMap((action) =>
        from(
          this.userService.downloadUsersTemplate({
            client_ids: action.client_ids,
            program_ids: action.program_ids ?? undefined,
            status: action.status ?? undefined,
            role_id: action.role_id ?? undefined,
            supervisor_id: action.supervisor_id ?? undefined,
          })
        ).pipe(
          map((response) => {
            this.alertService.success('Template downloaded', 'Success');
            const fileName = `User_Bulk_Update_Template.xlsx`;
            const blob = new Blob([response], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = globalThis.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            globalThis.URL.revokeObjectURL(url);
            action.showNextSignal.set(true);
            return UserActions.downloadUsersTemplateSuccess();
          }),
          catchError((error) => {
            console.error('Download users template error', error);
            return of(
              UserActions.downloadUsersTemplateFailure({ error: error.message })
            );
          })
        )
      )
    )
  );

  bulkUpdateUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.bulkUpdate),
      mergeMap((action) =>
        from(
          this.userService.bulkUpdateUsers(
            {
              client_ids: action.clientIds,
              program_ids: action.programIds,
              enable_ip_based_access: action.enableIpBasedAccess,
            },
            action.file
          )
        ).pipe(
          map((response) => {
            this.alertService.success(response.message, 'Success');
            this.router.navigate(['/users/bulk-results']);
            return UserActions.bulkUpdateSuccess({
              message: response.message,
              data: response.data,
            });
          }),
          catchError((error) => {
            console.log('Bulk update Error', error);
            return of(UserActions.bulkUpdateFailure({ error: error.message }));
          })
        )
      )
    )
  );
}
